import type { ReactNode } from 'react'
import { useState } from 'react'

import {
  IconCheck,
  IconFlask,
  IconLanguage,
  IconLogout,
  IconMoon,
  IconSelector,
  IconSun,
} from '@tabler/icons-react'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'

import { useUpdateLanguage } from '@/api/v1/administration/profile/update-language'
import { useUpdateTheme } from '@/api/v1/administration/profile/update-theme'
import { DrawerMenuItem } from '@/components/common/drawer-menu-item'
import { FeaturePaywallBadge } from '@/components/common/feature-paywall/feature-paywall-badge'
import {
  ResponsiveDialog,
  ResponsiveDialogBody,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
} from '@/components/common/responsive-dialog'
import { Avatar } from '@/components/sui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/sui/dropdown-menu'
import { Separator } from '@/components/sui/separator'
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/sui/sidebar'
import { useComponentTranslation } from '@/hooks/use-component-translation'
import { useFeaturePaywall } from '@/hooks/use-feature-paywall'
import { FEATURES, PLANS } from '@/lib/plans'
import { localeToLanguage, redirectBeforeContextSwitch } from '@/lib/utils'
import { useContextStore } from '@/stores/context'
import type { Locale } from '@/stores/language'
import { useLanguageStore } from '@/stores/language'
import { useSandboxStore } from '@/stores/sandbox'
import { useThemeStore } from '@/stores/theme'

import locales from './locales'

const LANGUAGES: Array<{ locale: Locale; labelKey: string }> = [
  { locale: 'deDe', labelKey: 'germanGermany' },
  { locale: 'enUs', labelKey: 'englishUS' },
  { locale: 'esEs', labelKey: 'spanishSpain' },
  { locale: 'frFr', labelKey: 'frenchFrance' },
  { locale: 'itIt', labelKey: 'italianItaly' },
  { locale: 'ptBr', labelKey: 'portugueseBrazil' },
]

const THEMES = [
  { theme: 'light', labelKey: 'light', Icon: IconSun },
  { theme: 'dark', labelKey: 'dark', Icon: IconMoon },
] as const

function DrawerGroupLabel({ children }: { children: ReactNode }) {
  return (
    <p className="text-muted-foreground px-2 pt-1.5 pb-1 text-xs">{children}</p>
  )
}

export function NavUser() {
  const { isMobile, setOpenMobile } = useSidebar()
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const queryClient = useQueryClient()
  const { locale, setLocale } = useLanguageStore()
  const isSandboxMode = useSandboxStore((state) => state.isSandboxMode)
  const setIsSandboxMode = useSandboxStore((state) => state.setIsSandboxMode)
  const accountContext = useContextStore((state) => state.userAccountContext)
  const theme = useThemeStore((state) => state.theme)
  const setThemeStore = useThemeStore((state) => state.setTheme)
  const { t } = useComponentTranslation(locales)
  const navigate = useNavigate()
  const updateLanguageMutation = useUpdateLanguage()
  const updateThemeMutation = useUpdateTheme()

  // Sandbox belongs to the primary (logged-in) account; the default gate already
  // uses the primary account, which is correct even while managing another.
  const { hasAccess: hasAccessToSandbox, requireAccess: requireSandboxAccess } =
    useFeaturePaywall(FEATURES.SANDBOX, PLANS.PRO)

  // Fetch user data from context
  const userName = accountContext?.userAccountInformation.name ?? ''
  const userEmail = accountContext?.userAccountInformation.email ?? ''
  const userAvatar = accountContext?.userAccountInformation.avatar ?? null
  const isSandboxEnabled =
    accountContext?.accountInformation.isSandboxEnabled ?? false
  const isManaged = accountContext?.accountInformation.isManaged ?? false

  // Second line of the trigger identifies the active account, on every origin.
  // The sidebar header can't stand in for it on a white-label origin: the logo
  // there belongs to the reseller, not to the account being used. The email is
  // still one click away, in the dropdown header below.
  //
  // A personal account is born named after its owner, so printing that name
  // here would only echo the line above — the type label reads better. That
  // holds just while the two match: a renamed account, a second personal
  // account, or someone else's that you were invited to all need their name
  // shown to be told apart.
  const accountName = accountContext?.accountInformation.name ?? ''
  const accountType = accountContext?.accountInformation.type
  const echoesUserName = accountName.trim() === userName.trim()
  const triggerSecondLine =
    accountType === 'Personal' && echoesUserName
      ? t('personalAccount')
      : accountName

  // The "Test mode" group advertises a PRO-gated capability. A managed account
  // without access can't act on the upsell, so hide the whole group for it
  // (still shown for a managed account that has access).
  const showSandboxGroup =
    isSandboxEnabled && (hasAccessToSandbox || !isManaged)

  const closeMenus = () => {
    setIsDrawerOpen(false)
    setOpenMobile(false)
  }

  const handleSandboxToggle = () => {
    closeMenus()
    requireSandboxAccess(async () => {
      const newSandboxMode = !isSandboxMode
      // Leave any record-scoped page (and clear an unsaved-changes prompt)
      // BEFORE switching scope, so the old page never refetches under the
      // new sandbox mode. If the user cancels the prompt, abort the switch.
      const canProceed = await redirectBeforeContextSwitch((to) =>
        navigate({ to }),
      )
      if (!canProceed) return
      setIsSandboxMode(newSandboxMode)
      queryClient.clear()
      window.location.reload()
    })
  }

  const handleLanguageChange = (newLocale: Locale) => {
    closeMenus()
    // Update local store (changes i18n automatically)
    setLocale(newLocale)

    // Update API (only if user is logged in)
    if (accountContext) {
      updateLanguageMutation.mutate(localeToLanguage(newLocale))
    }
  }

  const changeTheme = (newTheme: 'light' | 'dark') => {
    closeMenus()
    // Update local store (applies theme to DOM automatically)
    setThemeStore(newTheme)

    // Update API (only if user is logged in)
    if (accountContext) {
      const apiTheme = newTheme === 'dark' ? 'Dark' : 'Light'
      updateThemeMutation.mutate(apiTheme)
    }
  }

  const handleLogout = () => {
    closeMenus()
    navigate({ to: '/logout' })
  }

  const triggerContent = (
    <>
      <Avatar
        name={userName}
        src={userAvatar}
        className="h-8 w-8"
        shape="square"
      />
      <div className="grid flex-1 text-left text-sm leading-tight">
        <span className="truncate font-medium">{userName}</span>
        <span className="truncate text-xs">{triggerSecondLine}</span>
      </div>
      <IconSelector className="ml-auto size-4" />
    </>
  )

  const sandboxPaywallBadge = !hasAccessToSandbox && (
    <FeaturePaywallBadge minimumPlan={PLANS.PRO} className="ml-auto" />
  )

  if (isMobile) {
    return (
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton
            id="sidebar-user-menu"
            size="lg"
            className="cursor-pointer"
            onClick={() => setIsDrawerOpen(true)}
          >
            {triggerContent}
          </SidebarMenuButton>
          <ResponsiveDialog
            open={isDrawerOpen}
            onOpenChange={setIsDrawerOpen}
            surface="drawer"
          >
            <ResponsiveDialogContent>
              <ResponsiveDialogHeader visuallyHidden>
                <ResponsiveDialogTitle>{userName}</ResponsiveDialogTitle>
                <ResponsiveDialogDescription>
                  {userEmail}
                </ResponsiveDialogDescription>
              </ResponsiveDialogHeader>
              <ResponsiveDialogBody flush>
                <div className="flex items-center gap-2 px-3 py-2.5 text-left text-sm">
                  <Avatar
                    name={userName}
                    src={userAvatar}
                    className="h-8 w-8"
                    shape="square"
                  />
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-medium">{userName}</span>
                    <span className="truncate text-xs">{userEmail}</span>
                  </div>
                </div>
                <Separator />
                <div className="p-1">
                  <DrawerGroupLabel>{t('language')}</DrawerGroupLabel>
                  {LANGUAGES.map((language) => (
                    <DrawerMenuItem
                      key={language.locale}
                      icon={<IconLanguage />}
                      label={t(language.labelKey)}
                      isSelected={locale === language.locale}
                      onClick={() => handleLanguageChange(language.locale)}
                    />
                  ))}
                </div>
                <Separator />
                <div className="p-1">
                  <DrawerGroupLabel>{t('theme')}</DrawerGroupLabel>
                  {THEMES.map(({ theme: option, labelKey, Icon }) => (
                    <DrawerMenuItem
                      key={option}
                      icon={<Icon />}
                      label={t(labelKey)}
                      isSelected={theme === option}
                      onClick={() => changeTheme(option)}
                    />
                  ))}
                </div>
                {showSandboxGroup && (
                  <>
                    <Separator />
                    <div className="p-1">
                      <DrawerGroupLabel>{t('testMode')}</DrawerGroupLabel>
                      <DrawerMenuItem
                        icon={<IconFlask />}
                        label={isSandboxMode ? t('deactivate') : t('activate')}
                        trailing={sandboxPaywallBadge}
                        onClick={handleSandboxToggle}
                      />
                    </div>
                  </>
                )}
                <Separator />
                <div className="p-1">
                  <DrawerMenuItem
                    icon={<IconLogout />}
                    label={t('logout')}
                    onClick={handleLogout}
                  />
                </div>
              </ResponsiveDialogBody>
            </ResponsiveDialogContent>
          </ResponsiveDialog>
        </SidebarMenuItem>
      </SidebarMenu>
    )
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger
            render={
              <SidebarMenuButton
                id="sidebar-user-menu"
                size="lg"
                className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground cursor-pointer"
              />
            }
          >
            {triggerContent}
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-md"
            side="right"
            align="end"
            sideOffset={4}
          >
            <DropdownMenuGroup>
              <DropdownMenuLabel className="p-0 font-normal">
                <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                  <Avatar
                    name={userName}
                    src={userAvatar}
                    className="h-8 w-8"
                    shape="square"
                  />
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-medium">{userName}</span>
                    <span className="truncate text-xs">{userEmail}</span>
                  </div>
                </div>
              </DropdownMenuLabel>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuLabel className="text-muted-foreground text-xs">
                {t('language')}
              </DropdownMenuLabel>
              {LANGUAGES.map((language) => (
                <DropdownMenuItem
                  key={language.locale}
                  onClick={() => handleLanguageChange(language.locale)}
                  className="gap-2"
                >
                  <IconLanguage className="size-4" />
                  <span className="flex-1">{t(language.labelKey)}</span>
                  {locale === language.locale && (
                    <IconCheck className="size-4" />
                  )}
                </DropdownMenuItem>
              ))}
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuLabel className="text-muted-foreground text-xs">
                {t('theme')}
              </DropdownMenuLabel>
              {THEMES.map(({ theme: option, labelKey, Icon }) => (
                <DropdownMenuItem
                  key={option}
                  onClick={() => changeTheme(option)}
                  className="gap-2"
                >
                  <Icon className="size-4" />
                  <span className="flex-1">{t(labelKey)}</span>
                  {theme === option && <IconCheck className="size-4" />}
                </DropdownMenuItem>
              ))}
            </DropdownMenuGroup>
            {showSandboxGroup && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuGroup>
                  <DropdownMenuLabel className="text-muted-foreground text-xs">
                    {t('testMode')}
                  </DropdownMenuLabel>
                  <DropdownMenuItem
                    onClick={handleSandboxToggle}
                    className="gap-2"
                  >
                    <IconFlask className="size-4" />
                    <span className="flex-1">
                      {isSandboxMode ? t('deactivate') : t('activate')}
                    </span>
                    {sandboxPaywallBadge}
                  </DropdownMenuItem>
                </DropdownMenuGroup>
              </>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout}>
              <IconLogout />
              {t('logout')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
